import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ComposerUpload, UploadStatus } from 'components/ComposerAttachments'
import { deleteChatAttachment, uploadChatAttachment } from 'api/chat-attachments'
import { useChatAttachmentParsing } from 'hooks/useChatAttachmentParsing'
import { showErrorToast } from 'utils/toast'
import { ChatAttachment, ChatAttachmentStatusEnum } from 'types/chat-attachment'

type UseComposerAttachmentsOptions = {
  chatId: string | undefined
  isInOperatorMode: boolean
}

/**
 * Composer upload list. Operator mode keeps files local (sent with the message);
 * bot mode uploads immediately, then waits for server-side parsing over WS.
 */
export const useComposerAttachments = ({ chatId, isInOperatorMode }: UseComposerAttachmentsOptions) => {
  const { t } = useTranslation()
  const [uploads, setUploads] = useState<ComposerUpload[]>([])
  const idRef = useRef(0)

  const isLoadingAttachments = uploads.some((upload) => upload.status !== 'ready')

  const onFilesAdded = useCallback(
    (newFiles: File[]) => {
      newFiles.forEach((file) => {
        const id = String(idRef.current++)
        if (isInOperatorMode) {
          setUploads((prev) => [...prev, { id, file, status: 'ready' }])
          return
        }
        if (!chatId) return
        setUploads((prev) => [...prev, { id, file, status: 'uploading' }])
        uploadChatAttachment(file, chatId)
          .then((attachment) => {
            const status: UploadStatus = attachment.status === ChatAttachmentStatusEnum.READY ? 'ready' : 'processing'
            setUploads((prev) => prev.map((upload) => (upload.id === id ? { ...upload, status, attachment } : upload)))
          })
          .catch((e) => {
            console.error({ e })
            showErrorToast(t('chatAttachmentUploadError'))
            setUploads((prev) => prev.filter((upload) => upload.id !== id))
          })
      })
    },
    [isInOperatorMode, chatId, t],
  )

  // Removing a bot attachment deletes it server-side (privacy); operator files are only local.
  const onRemove = useCallback((id: string) => {
    setUploads((prev) => {
      const target = prev.find((upload) => upload.id === id)
      if (target?.attachment) {
        deleteChatAttachment(target.attachment._id).catch((e) => console.error({ e }))
      }
      return prev.filter((upload) => upload.id !== id)
    })
  }, [])

  useChatAttachmentParsing(
    uploads
      .filter((upload) => upload.status === 'processing' && upload.attachment)
      .map((upload) => upload.attachment!._id),
    {
      onReady: (attachmentId) =>
        setUploads((prev) =>
          prev.map((upload) => (upload.attachment?._id === attachmentId ? { ...upload, status: 'ready' } : upload)),
        ),
      onFailed: (attachmentId) => {
        showErrorToast(t('chatAttachmentFailed'))
        setUploads((prev) => prev.filter((upload) => upload.attachment?._id !== attachmentId))
      },
    },
  )

  /** Bot send: hand over the uploaded attachments and clear the list. Clearing does NOT
   *  delete them server-side — they are now committed to the message. */
  const takeAttachments = useCallback((): ChatAttachment[] => {
    const attachments = uploads.map((upload) => upload.attachment).filter(Boolean) as ChatAttachment[]
    setUploads([])
    return attachments
  }, [uploads])

  /** Operator send: hand over the local files and clear the list. */
  const takeFiles = useCallback((): File[] => {
    const files = uploads.map((upload) => upload.file)
    setUploads([])
    return files
  }, [uploads])

  return { uploads, isLoadingAttachments, onFilesAdded, onRemove, takeAttachments, takeFiles }
}
